import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import html2pdf from 'html2pdf.js';
import { Heart, Clock, AlertTriangle, Syringe, Download, RotateCcw, Sparkles, Loader2, Zap, Activity } from 'lucide-react';

import { API_URL } from '../config';

export default function NSTEMIPage() {
  const { token } = useAuth();
  const printRef = useRef(null);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  
  const [formData, setFormData] = useState(() => {
    try {
      const saved = localStorage.getItem('nstemi-data');
      return saved ? JSON.parse(saved) : {
        criteresUrgents: [],
        criteres24h: [],
        grace: { age: '', fc: '', pas: '', creatinine: '', killip: '', acr: false, modifST: false, troponine: false }
      };
    } catch {
      return {
        criteresUrgents: [],
        criteres24h: [],
        grace: { age: '', fc: '', pas: '', creatinine: '', killip: '', acr: false, modifST: false, troponine: false }
      };
    }
  });

  useEffect(() => {
    localStorage.setItem('nstemi-data', JSON.stringify(formData));
  }, [formData]);

  const handleAnalyzeAI = async () => {
    try {
      setIsAnalyzing(true);
      toast.info('Analyse en cours...');
      const response = await fetch(`${API_URL}/api/ai/analyze-nstemi`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(formData)
      });
      if (!response.ok) throw new Error('Erreur');
      const data = await response.json();
      setAiAnalysis(data);
      toast.success('Analyse terminee');
    } catch (error) {
      toast.error('Erreur analyse');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const toggleCritere = (section, critere) => {
    setFormData(prev => ({
      ...prev,
      [section]: prev[section].includes(critere) ? prev[section].filter(c => c !== critere) : [...prev[section], critere]
    }));
  };

  const hasUrgent = formData.criteresUrgents.length > 0;

  // Calculate GRACE score
  const age = parseInt(formData.grace.age) || 0;
  const fc = parseInt(formData.grace.fc) || 0;
  const pas = parseInt(formData.grace.pas) || 0;
  const creat = parseInt(formData.grace.creatinine) || 0;
  const killipPts = formData.grace.killip === 'IV' ? 60 : formData.grace.killip === 'III' ? 40 : formData.grace.killip === 'II' ? 20 : 0;
  
  let graceScore = Math.round(age/2) + Math.round(fc/5) + Math.round((200-pas)/10) + Math.round(creat/40) + killipPts;
  graceScore += formData.grace.acr ? 40 : 0;
  graceScore += formData.grace.modifST ? 30 : 0;
  graceScore += formData.grace.troponine ? 15 : 0;
  
  const risque = graceScore > 140 ? 'HAUT' : graceScore > 109 ? 'INTERMEDIAIRE' : 'BAS';
  const delai = graceScore > 140 ? '< 24H' : graceScore > 109 ? '< 72H' : 'Programmee';
  const color = graceScore > 140 ? 'red' : graceScore > 109 ? 'amber' : 'green';

  return (
    <div className="p-3 md:p-6 space-y-3 max-w-5xl mx-auto">
      <div className="space-y-2 print:hidden">
        <div className="flex justify-between items-start gap-2">
          <div className="flex items-center gap-2">
            <Heart className="w-6 h-6 text-orange-400" />
            <div>
              <h1 className="text-lg md:text-2xl font-bold text-white">NSTEMI</h1>
              <p className="text-xs text-gray-400">SCA Non ST+</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleAnalyzeAI} disabled={isAnalyzing} size="sm" className="bg-purple-500/20">
              {isAnalyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            </Button>
            <Button onClick={() => { localStorage.removeItem('nstemi-data'); window.location.reload(); }} size="sm" variant="outline">
              <RotateCcw className="w-4 h-4" />
            </Button>
          </div>
        </div>
        {hasUrgent && (
          <Alert className="bg-red-500/20 border-red-500 p-2 animate-pulse">
            <Zap className="h-4 w-4" />
            <AlertDescription className="text-xs text-red-300 font-bold">CORONAROGRAPHIE URGENTE &lt; 2H</AlertDescription>
          </Alert>
        )}
      </div>

      <div ref={printRef}>
        <Tabs defaultValue="indications">
          <TabsList className="grid w-full grid-cols-4 bg-gray-800">
            <TabsTrigger value="indications" className="text-xs">Indications</TabsTrigger>
            <TabsTrigger value="depistage" className="text-xs">Dépistage HR</TabsTrigger>
            <TabsTrigger value="traitements" className="text-xs">Traitements</TabsTrigger>
            <TabsTrigger value="grace" className="text-xs">Score GRACE</TabsTrigger>
          </TabsList>

          <TabsContent value="indications" className="space-y-3 mt-3">
            <Card className="border-red-500/30 bg-red-500/5">
              <CardHeader className="p-3">
                <CardTitle className="text-sm text-red-300 flex items-center gap-2">
                  <Clock className="w-4 h-4" />CORONARO URGENTE (&lt; 2H)
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3 space-y-2">
                {['Angor refractaire', 'Instabilite hemodynamique', 'Instabilite rythmique', 'Modif electriques persistantes', 'Complications mecaniques'].map((c, i) => (
                  <label key={i} className="flex items-center gap-2 p-2 rounded bg-gray-800 cursor-pointer">
                    <Checkbox checked={formData.criteresUrgents.includes(c)} onCheckedChange={() => toggleCritere('criteresUrgents', c)} />
                    <span className="text-xs text-white">{c}</span>
                  </label>
                ))}
              </CardContent>
            </Card>

            <Card className="border-amber-500/30 bg-amber-500/5">
              <CardHeader className="p-3">
                <CardTitle className="text-sm text-amber-300 flex items-center gap-2">
                  <Clock className="w-4 h-4" />CORONARO 24H
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3 space-y-2">
                {['ACR sans sus-decalage ST', 'Elevation troponine', 'Modif ECG ST/T', 'Score GRACE > 140'].map((c, i) => (
                  <label key={i} className="flex items-center gap-2 p-2 rounded bg-gray-800 cursor-pointer">
                    <Checkbox checked={formData.criteres24h.includes(c)} onCheckedChange={() => toggleCritere('criteres24h', c)} />
                    <span className="text-xs text-white">{c}</span>
                  </label>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="depistage" className="space-y-3 mt-3">
            <Card className="border-cyan-500/30 bg-cyan-500/5">
              <CardHeader className="p-3">
                <CardTitle className="text-sm text-cyan-300">DEPISTAGE SCA HAUT RISQUE</CardTitle>
                <p className="text-xs text-gray-400 mt-1">Criteres ECG specifiques</p>
              </CardHeader>
              <CardContent className="p-3 space-y-3">
                <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30">
                  <div className="text-xs font-semibold text-red-300 mb-2">1. SOUS-DECALAGE ST DIFFUS</div>
                  <div className="space-y-1 text-xs text-gray-300">
                    <div>• ST en aVR &gt; 0,5mm ET/OU V1</div>
                    <div>• ST &gt; 1mm dans au moins 6 derivations</div>
                  </div>
                  <Alert className="mt-2 bg-red-500/20 border-red-500/40 p-2">
                    <AlertTriangle className="h-3 w-3" />
                    <AlertDescription className="text-xs text-red-300">
                      Compatible : Occlusion IVA proximale, Tronc commun, Lesions tritronculaires
                    </AlertDescription>
                  </Alert>
                </div>

                <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
                  <div className="text-xs font-semibold text-amber-300 mb-2">2. SOUS-DECALAGE ISOLE V1-V3</div>
                  <div className="space-y-1 text-xs text-gray-300">
                    <div>• ST isole V1-V3</div>
                    <div>• Onde R &gt; S en V1</div>
                  </div>
                  <Alert className="mt-2 bg-amber-500/20 border-amber-500/40 p-2">
                    <AlertDescription className="text-xs text-amber-300">
                      Compatible : Infarctus basal (territoire posterieur)
                    </AlertDescription>
                  </Alert>
                </div>

                <div className="p-3 rounded-lg bg-purple-500/10 border border-purple-500/30">
                  <div className="text-xs font-semibold text-purple-300 mb-2">3. ONDE T DE WINTER</div>
                  <div className="space-y-1 text-xs text-gray-300">
                    <div>• ST ascendant depuis point J sous-decale 1-3mm</div>
                    <div>• Onde T ample, positive, symetrique</div>
                    <div>• Territoire precordial</div>
                  </div>
                  <Alert className="mt-2 bg-red-500/30 border-red-500 p-2 animate-pulse">
                    <Zap className="h-4 w-4 text-red-400" />
                    <AlertDescription className="text-xs text-red-300 font-bold">
                      URGENCE ABSOLUE - Occlusion proximale IVA - Coronaro IMMEDIATE
                    </AlertDescription>
                  </Alert>
                </div>

                <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/30">
                  <div className="text-xs font-semibold text-blue-300 mb-2">4. GRADES ISCHEMIQUES (Taboulet & Smith)</div>
                  <div className="space-y-2">
                    <div className="p-2 rounded bg-gray-800">
                      <div className="text-xs text-green-300 font-medium">Grade 1 (0-30 min)</div>
                      <div className="text-xs text-gray-400">Grande onde T ample et symetrique</div>
                    </div>
                    <div className="p-2 rounded bg-gray-800">
                      <div className="text-xs text-amber-300 font-medium">Grade 2 (quelques heures)</div>
                      <div className="text-xs text-gray-400">Elevation ST (extension sous-epicardique)</div>
                    </div>
                    <div className="p-2 rounded bg-gray-800">
                      <div className="text-xs text-red-300 font-medium">Grade 3 (plusieurs heures)</div>
                      <div className="text-xs text-gray-400">Anomalies QRS (necrose transmurale)</div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="traitements" className="space-y-3 mt-3">
            <Card className="border-purple-500/30">
              <CardHeader className="p-3">
                <CardTitle className="text-sm text-purple-300 flex items-center gap-2">
                  <Syringe className="w-4 h-4" />TRAITEMENTS
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3 space-y-2">
                {[
                  { nom: 'ASPIRINE', dose: '250 mg IV' },
                  { nom: 'ENOXAPARINE', dose: '1mg/kg SC x2/j' },
                  { nom: 'DERIVES NITRES', dose: 'SL et/ou IVSE' },
                  { nom: 'RISORDAN', dose: 'PAS 120 mmHg' }
                ].map((t, i) => (
                  <div key={i} className="p-2 rounded bg-gray-800 border border-gray-700 flex justify-between">
                    <span className="text-sm text-white">{t.nom}</span>
                    <Badge className="bg-purple-500/20 text-purple-300 font-mono text-xs">{t.dose}</Badge>
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="grace" className="space-y-3 mt-3">
            <Card className="border-blue-500/30 bg-blue-500/5">
              <CardHeader className="p-3">
                <CardTitle className="text-sm text-blue-300">SCORE GRACE</CardTitle>
              </CardHeader>
              <CardContent className="p-3 space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs text-gray-400 mb-1 block">Age (ans)</label>
                    <Input
                      type="number"
                      value={formData.grace.age}
                      onChange={(e) => setFormData(prev => ({ ...prev, grace: { ...prev.grace, age: e.target.value } }))}
                      className="h-8 text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-400 mb-1 block">FC (bpm)</label>
                    <Input
                      type="number"
                      value={formData.grace.fc}
                      onChange={(e) => setFormData(prev => ({ ...prev, grace: { ...prev.grace, fc: e.target.value } }))}
                      className="h-8 text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-400 mb-1 block">PAS (mmHg)</label>
                    <Input
                      type="number"
                      value={formData.grace.pas}
                      onChange={(e) => setFormData(prev => ({ ...prev, grace: { ...prev.grace, pas: e.target.value } }))}
                      className="h-8 text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-400 mb-1 block">Creatinine</label>
                    <Input
                      type="number"
                      value={formData.grace.creatinine}
                      onChange={(e) => setFormData(prev => ({ ...prev, grace: { ...prev.grace, creatinine: e.target.value } }))}
                      className="h-8 text-sm"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs text-gray-400 mb-2 block">Classe Killip</label>
                  <div className="grid grid-cols-4 gap-2">
                    {['I', 'II', 'III', 'IV'].map((k) => (
                      <button
                        key={k}
                        onClick={() => setFormData(prev => ({ ...prev, grace: { ...prev.grace, killip: k } }))}
                        className={`px-3 py-2 text-xs rounded-lg ${formData.grace.killip === k ? 'bg-blue-500 text-white' : 'bg-gray-800 text-gray-400'}`}
                      >
                        {k}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="flex items-center gap-2 cursor-pointer p-2 rounded bg-gray-800">
                    <Checkbox checked={formData.grace.acr} onCheckedChange={(c) => setFormData(prev => ({ ...prev, grace: { ...prev.grace, acr: c } }))} />
                    <span className="text-xs text-white">ACR admission (+40 pts)</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer p-2 rounded bg-gray-800">
                    <Checkbox checked={formData.grace.modifST} onCheckedChange={(c) => setFormData(prev => ({ ...prev, grace: { ...prev.grace, modifST: c } }))} />
                    <span className="text-xs text-white">Modification ST (+30 pts)</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer p-2 rounded bg-gray-800">
                    <Checkbox checked={formData.grace.troponine} onCheckedChange={(c) => setFormData(prev => ({ ...prev, grace: { ...prev.grace, troponine: c } }))} />
                    <span className="text-xs text-white">Troponine elevee (+15 pts)</span>
                  </label>
                </div>

                <div className={`p-4 rounded-lg border-2 ${color === 'red' ? 'bg-red-500/20 border-red-500' : color === 'amber' ? 'bg-amber-500/20 border-amber-500' : 'bg-green-500/20 border-green-500'}`}>
                  <div className="text-center space-y-2">
                    <div className="text-xs text-blue-300 font-semibold">SCORE TOTAL</div>
                    <div className={`text-4xl font-bold font-mono ${color === 'red' ? 'text-red-400' : color === 'amber' ? 'text-amber-400' : 'text-green-400'}`}>
                      {graceScore}
                    </div>
                    <Badge className={color === 'red' ? 'bg-red-500' : color === 'amber' ? 'bg-amber-500' : 'bg-green-500'}>{risque} RISQUE</Badge>
                    <div className="text-xs text-gray-300">Coronarographie : <strong className="text-cyan-300">{delai}</strong></div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {aiAnalysis && (
          <Card className="border-purple-500/30 bg-purple-500/5 mt-3">
            <CardHeader className="p-3">
              <CardTitle className="text-sm text-purple-300 flex items-center gap-2">
                <Sparkles className="w-4 h-4" />ANALYSE IA
              </CardTitle>
            </CardHeader>
            <CardContent className="p-3">
              <div className="whitespace-pre-wrap text-xs text-white">{aiAnalysis.analysis}</div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
